import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  CalendarToday,
  ChatBubble,
  ChevronRight,
  Description,
  LocalHospital,
  OpenInNew,
  Person,
  Psychology,
  Star,
  RateReview,
} from '@mui/icons-material';
import { Badge, Button, Card, CardContent, CardHeader } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { DebugCard } from '../components/debug-card';
import { EncounterTelemetryCard } from '../components/encounter-telemetry-card';
import { LoadingState } from '../components/loading-state';
import { FeedbackSessionDetailDialog } from '../components/feedback-session-detail-dialog';
import { useGetNoteDetailsQuery, useGetProviderNoteProcessingSessionsQuery } from '../store/api';
import { enhancedApi as adminApi } from '@store/generated/admin';
import { formatDateTime } from '../utils/formatting';

const { useGetV2AdminFeedbackSessionsByNoteByNoteIdQuery } = adminApi;

export default function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const [selectedFeedbackSessionId, setSelectedFeedbackSessionId] = useState<string | null>(null);

  // Call hook unconditionally (React rules of hooks)
  const { data, isLoading } = useGetNoteDetailsQuery({ noteId: noteId || '' }, { skip: !noteId });

  // Extract providerId from the note detail response
  const providerId = data?.provider?.provider_id || data?.note?.provider_id;

  // Get related note processing sessions for this note
  const { data: npsData } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      noteId: noteId || '',
      perPage: 10,
    },
    { skip: !noteId || !providerId },
  );

  // Get feedback sessions for this note
  const { data: feedbackData } = useGetV2AdminFeedbackSessionsByNoteByNoteIdQuery(
    { noteId: noteId || '' },
    { skip: !noteId },
  );

  if (!noteId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid URL parameters</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading note details..." />;
  }

  if (!data?.note) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Note not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const { note, provider, transcriptions, encounter } = data;
  // Get the first transcription for display
  const transcript = transcriptions?.[0];

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>

        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {provider.name}
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  Note {noteId}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Note Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Note {noteId}</h1>
                  <p className="text-gray-600">{note.template}</p>
                  <p className="text-sm text-gray-500">
                    Created: {formatDateTime(note.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => window.open(`/admin/note/${noteId}`, '_blank')}
                  className="flex items-center"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  See full note 🔎
                </Button>
                <Badge color={getStatusBadgeColor(note.status)}>
                  {note.status.replace('_', ' ')}
                </Badge>
                {feedbackData && feedbackData.feedback_session_id ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RateReview />}
                    onClick={() => setSelectedFeedbackSessionId(feedbackData.feedback_session_id)}
                  >
                    View Feedback Details
                  </Button>
                ) : (
                  <Badge color="secondary">No Feedback</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Telemetry Summary */}
        <div className="mt-6 mb-6">
          <EncounterTelemetryCard noteId={noteId} encounterId={encounter?.encounter_id} />
        </div>

        {/* Feedback Section */}
        {note.has_feedback && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <ChatBubble className="mr-2 h-5 w-5" />
                User Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              {note.feedback_rating && (
                <div className="flex items-center mb-4">
                  <span className="text-sm font-medium text-gray-500 mr-2">Rating:</span>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= (note.feedback_rating ?? 0)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-900">{note.feedback_rating}/5</span>
                  </div>
                </div>
              )}
              {note.feedback_text && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Feedback:</span>
                  <p className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {note.feedback_text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Feedback Session Detail Dialog */}
        {selectedFeedbackSessionId && (
          <FeedbackSessionDetailDialog
            open={!!selectedFeedbackSessionId}
            onClose={() => setSelectedFeedbackSessionId(null)}
            sessionId={selectedFeedbackSessionId}
          />
        )}

        {/* Debug Card */}
        <DebugCard
          transcriptionId={transcript?.transcription_session_id}
          noteProcessingId={npsData?.items?.[0]?.note_processing_session_id}
          noteId={noteId}
        />

        {/* Related Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
          {/* Provider Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Person className="mr-2 h-5 w-5" />
                Provider Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Name:</span>
                  <span className="text-sm text-gray-900">{provider.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Email:</span>
                  <span className="text-sm text-gray-900">{provider.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Provider ID:</span>
                  <span className="text-sm text-gray-900">{providerId}</span>
                </div>
                <div className="mt-4">
                  <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                    <Button variant="outlined" size="small" className="w-full">
                      View Provider Details
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Encounter Information */}
          {encounter && encounter.encounter_id ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <LocalHospital className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Encounter ID:</span>
                    <span className="text-sm text-gray-900">{encounter.encounter_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Type:</span>
                    <span className="text-sm text-gray-900">{encounter.encounter_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Patient:</span>
                    <span className="text-sm text-gray-900">{encounter.patient_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <Badge color={getStatusBadgeColor(encounter.status)}>
                      {encounter.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  {encounter.start_time && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Start Time:</span>
                      <span className="text-sm text-gray-900">
                        {formatDateTime(encounter.start_time)}
                      </span>
                    </div>
                  )}
                  {encounter.end_time && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">End Time:</span>
                      <span className="text-sm text-gray-900">
                        {formatDateTime(encounter.end_time)}
                      </span>
                    </div>
                  )}
                  {encounter.duration > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Duration:</span>
                      <span className="text-sm text-gray-900">{encounter.duration} minutes</span>
                    </div>
                  )}
                  <div className="mt-4">
                    <Link to={`/admin-v2/encounter/${encounter.encounter_id}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Encounter Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <LocalHospital className="mr-2 h-5 w-5" />
                  Related Encounter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  This note is not associated with an encounter.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Transcript Information */}
          {transcript ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Related Transcript
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Created:</span>
                    <span className="text-sm text-gray-900">
                      {formatDateTime(transcript.start_time || '')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Chunks:</span>
                    <span className="text-sm text-gray-900">{transcript.chunk_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <Badge color={getStatusBadgeColor(transcript.job_status)}>
                      {transcript.job_status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Notes Created:</span>
                    <span className="text-sm text-gray-900">{transcript.notes_created}</span>
                  </div>
                  <div className="mt-4">
                    <Link to={`/admin-v2/transcript/${transcript.transcription_session_id}`}>
                      <Button variant="outlined" size="small" className="w-full">
                        View Transcript Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Description className="mr-2 h-5 w-5" />
                  Related Transcript
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  This note was not created from a transcript.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Note Processing Session Information */}
        {npsData && npsData.items && npsData.items.length > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Psychology className="mr-2 h-5 w-5" />
                Note Processing Session
              </h3>
            </div>
            <div className="p-6">
              {npsData.items.map((nps: any) => (
                <div
                  key={nps.note_processing_session_id}
                  className="border rounded-lg p-4 mb-4 last:mb-0"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Session ID:</span>
                      <span className="text-sm text-gray-900">
                        {nps.note_processing_session_id.substring(0, 8)}...
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Status:</span>
                      <Badge color={getStatusBadgeColor(nps.status)}>
                        {nps.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Transcripts:</span>
                      <span className="text-sm text-gray-900">
                        {nps.transcription_session_ids?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">
                        {formatDateTime(nps.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`/admin-v2/note-processing-session/${nps.note_processing_session_id}`}
                    >
                      <Button variant="outlined" size="small" className="w-full">
                        View Session Details
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appointment Information */}
        {encounter && encounter.appointment_id && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <CalendarToday className="mr-2 h-5 w-5" />
                Related Appointment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Appointment ID:</span>
                  <span className="text-sm text-gray-900">{encounter.appointment_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Patient:</span>
                  <span className="text-sm text-gray-900">{encounter.patient_name}</span>
                </div>
                {encounter.patient_id && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Patient ID:</span>
                    <span className="text-sm text-gray-900">{encounter.patient_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Type:</span>
                  <span className="text-sm text-gray-900">{encounter.encounter_type}</span>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/admin-v2/appointment/${encounter.appointment_id}`}>
                  <Button variant="outlined" size="small">
                    View Appointment Details
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
